import { Globe, Sparkles, Heart, MessageCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";

interface SparksTimelineProps {
  matches: Array<{
    id: string;
    match_source: string;
    created_at: string;
    matchedProfile: {
      id: string;
      name: string;
      photo_url: string | null;
    } | null;
    source_context?: Record<string, unknown> | null;
  }>;
  onMatchClick: (matchId: string) => void;
  className?: string;
}

export const SparksTimeline = ({ matches, onMatchClick, className }: SparksTimelineProps) => {
  const timeline = useMemo(() => {
    return matches
      .filter((m) => m.matchedProfile)
      .map((match) => {
        const matchDate = new Date(match.created_at);
        const daysAgo = (Date.now() - matchDate.getTime()) / (1000 * 60 * 60 * 24);
        
        // Determine milestone type
        let milestone = "matched";
        let label = "Matched";
        let icon = Heart;
        
        if (daysAgo <= 1) {
          milestone = "recent";
          label = "Just matched";
        } else if (daysAgo <= 7) {
          milestone = "week";
          label = "Matched this week";
        } else if (daysAgo <= 30) {
          milestone = "month";
          label = "Matched this month";
        }

        // Get location for Global Connect matches
        const context = match.source_context as Record<string, string> | null;
        let location = null;
        if (match.match_source === "global_connect") {
          location = context?.city_name || context?.country_code || null;
        }

        return {
          match,
          milestone,
          label,
          icon,
          timeAgo: formatDistanceToNow(matchDate, { addSuffix: true }),
          location,
        };
      })
      .sort((a, b) => new Date(b.match.created_at).getTime() - new Date(a.match.created_at).getTime());
  }, [matches]);

  if (timeline.length === 0) {
    return (
      <div className={cn("text-center py-12", className)}>
        <p className="text-muted-foreground text-sm">No matches yet</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {timeline.map((item, index) => {
        const profile = item.match.matchedProfile;
        if (!profile) return null;

        return (
          <button
            key={item.match.id}
            onClick={() => onMatchClick(item.match.id)}
            className={cn(
              "w-full flex items-start gap-3 p-4 rounded-xl border transition-all",
              "bg-card/50 hover:bg-card/80 border-border/50 hover:border-primary/30",
              "text-left animate-fade-in-up"
            )}
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {/* Avatar */}
            <div className="relative shrink-0">
              {profile.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt={profile.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-background"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                  {profile.name[0]}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                <item.icon className="w-3 h-3 text-primary" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-foreground truncate">{profile.name}</h4>
                <span className="text-xs text-muted-foreground">{item.timeAgo}</span>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-primary">{item.label}</span>
                {item.location && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Globe className="w-3 h-3" />
                    <span>{item.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Mini globe pin for Global Connect */}
            {item.match.match_source === "global_connect" && item.location && (
              <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Globe className="w-4 h-4 text-blue-500" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

